import { IconBin, IconClose, IconPlus } from "@synthesis/shared-icons";
import { Button, Modal, SelectNew, selectItemDisplayLabel, setPromiseSnack } from "@synthesis/shared-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import { invalidateInternalApplicationQueries } from "../../../shared/utils/invalidate-application-queries";
import { usePageStore } from "../application-detail-page.lib";
import type { AssignRegionDepartmentsPopupAction } from "./application-control-popup.lib";
import {
  createEmptyDepartmentRow,
  createRegionDepartmentServerSearch,
  fetchRegionalDepartments,
  getInitialDepartmentRows,
  getRegionDepartmentLabel,
  getRegionDepartmentSnackMessages,
  getSelectedDepartments,
  isDuplicateRegionalDepartmentError,
  REGION_DEPARTMENTS_DUPLICATE_SNACK_MESSAGE,
  regionalDepartmentsQueryKey,
  saveRegionDepartments,
  type RegionDepartment,
  type RegionDepartmentRow,
} from "./application-control-region-departments-modal.lib";

const RegionDepartmentsModal = observer(({ popupAction }: { popupAction: AssignRegionDepartmentsPopupAction }) => {
  const store = usePageStore();
  const queryClient = useQueryClient();

  const { data: serverDepartments } = useQuery({
    queryKey: regionalDepartmentsQueryKey(popupAction.requestId),
    queryFn: ({ signal }) => fetchRegionalDepartments(popupAction.requestId, signal),
  });

  const initialDepartments = serverDepartments ?? store?.application?.regionDepartments ?? [];
  const hasExistingDepartments = initialDepartments.length > 0;

  const [knownDepartments, setKnownDepartments] = useState<RegionDepartment[]>(initialDepartments);
  const [rows, setRows] = useState<RegionDepartmentRow[]>(() => getInitialDepartmentRows(initialDepartments));
  const [departmentNameMap, setDepartmentNameMap] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const departmentServerSearch = useMemo(() => createRegionDepartmentServerSearch(), []);

  useEffect(() => {
    const nextDepartments = serverDepartments ?? store?.application?.regionDepartments ?? [];

    setKnownDepartments(nextDepartments);
    setRows(getInitialDepartmentRows(nextDepartments));
    setRowErrors({});

    const nameMap: Record<string, string> = {};
    for (const dept of nextDepartments) {
      nameMap[dept.id] = dept.name;
    }
    setDepartmentNameMap(nameMap);
  }, [popupAction, serverDepartments, store?.application?.regionDepartments]);

  const selectedDepartments = useMemo(() => getSelectedDepartments(rows, departmentNameMap), [rows, departmentNameMap]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      store?.setPopupAction(null);
    }
  };

  const handleDepartmentChange = (rowId: string, departmentId: string, departmentName: string) => {
    setRows((currentRows) => currentRows.map((row) => (row.rowId === rowId ? { ...row, departmentId } : row)));

    if (departmentId && departmentName) {
      setDepartmentNameMap((prev) => ({ ...prev, [departmentId]: departmentName }));
    }

    if (departmentId) {
      setRowErrors((prev) => {
        if (!(rowId in prev)) {
          return prev;
        }

        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }
  };

  const handleRemoveRow = (rowId: string) => {
    setRows((currentRows) => {
      const nextRows = currentRows.filter((row) => row.rowId !== rowId);

      return nextRows.length > 0 ? nextRows : [createEmptyDepartmentRow()];
    });

    setRowErrors((prev) => {
      if (!(rowId in prev)) {
        return prev;
      }

      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const handleAddRow = () => {
    setRows((currentRows) => [...currentRows, createEmptyDepartmentRow()]);
  };

  const isSubmitDisabled = isSubmitting || rows.some((row) => row.departmentId.length === 0);

  const handleSubmit = () => {
    const emptyRows = rows.filter((row) => row.departmentId.length === 0);

    if (emptyRows.length > 0) {
      setRowErrors(Object.fromEntries(emptyRows.map((row) => [row.rowId, "Выберите подразделение"])));
      return;
    }

    const snackMessages = getRegionDepartmentSnackMessages(hasExistingDepartments);

    const promise = (async () => {
      setIsSubmitting(true);

      try {
        await saveRegionDepartments(popupAction.requestId, selectedDepartments);
        await queryClient.invalidateQueries({ queryKey: regionalDepartmentsQueryKey(popupAction.requestId) });
        await invalidateInternalApplicationQueries(queryClient, popupAction.requestId);
        store?.setPopupAction(null);
      } finally {
        setIsSubmitting(false);
      }
    })();

    setPromiseSnack({
      promise,
      // errorResolver кладёт серверный текст в description. Для дублей гасим rejected-заголовок,
      // чтобы в снэкбаре осталось только «Отправка невозможна: есть повторяющиеся подразделения.».
      errorResolver: (error) => (isDuplicateRegionalDepartmentError(error) ? REGION_DEPARTMENTS_DUPLICATE_SNACK_MESSAGE : ""),
      promiseSettings: {
        title: {
          ...snackMessages,
          rejected: (errorMessage) => (typeof errorMessage === "string" && errorMessage.length > 0 ? "" : snackMessages.rejected),
        },
      },
    });
  };

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>Назначение ответственных в регионе</Modal.Title>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-6 p-8">
            <h3 className="text-type-common subtitle-lg after:text-type-error whitespace-nowrap after:content-['*']">
              Назначьте технический отдел региона
            </h3>

            <div className="border-neutrals-90 overflow-hidden rounded-[10px] border">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="bg-neutrals-99">
                    <th className="paragraph-sm text-type-minor px-4 py-3 text-left font-medium">Подразделение</th>
                    <th className="w-[180px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const placeholderLabel = getRegionDepartmentLabel(row.departmentId, knownDepartments);
                    const isPersistedDepartment = knownDepartments.some((department) => department.id === row.departmentId);
                    const rowActionLabel = isPersistedDepartment ? "Удалить" : "Отменить";
                    const RowActionIcon = isPersistedDepartment ? IconBin : IconClose;

                    return (
                      <tr key={row.rowId} className="border-neutrals-90 border-t align-top">
                        <td className="px-4 py-3">
                          <SelectNew
                            name={`region-department-${popupAction.requestId}-${row.rowId}`}
                            placeholder="Выберите из списка"
                            value={row.departmentId}
                            selectedItemLabel={departmentNameMap[row.departmentId] || placeholderLabel}
                            error={rowErrors[row.rowId]}
                            onChange={(value, item) => {
                              const label = item ? selectItemDisplayLabel(item) : "";
                              handleDepartmentChange(row.rowId, value, label);
                            }}
                            disabled={isSubmitting}
                            serverSearch={{
                              ...departmentServerSearch,
                              ...(placeholderLabel ? { placeholderLabel } : {}),
                            }}
                            size="md"
                            visibleItemsCount={6}
                            onClear={() => {
                              handleDepartmentChange(row.rowId, "", "");
                            }}
                            contentProps={{ style: { zIndex: 50 } }}
                            className="w-full min-w-0"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            type="button"
                            theme="secondary"
                            size="md"
                            disabled={isSubmitting}
                            leftIcon={<RowActionIcon className="text-type-minor size-4" aria-hidden />}
                            onClick={() => handleRemoveRow(row.rowId)}
                          >
                            {rowActionLabel}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Button
              type="button"
              theme="secondary"
              size="md"
              disabled={isSubmitting}
              leftIcon={<IconPlus className="size-4" aria-hidden />}
              className="self-start"
              onClick={handleAddRow}
            >
              Добавить подразделение
            </Button>
          </div>
        </div>

        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button type="button" theme="primary" size="lg" disabled={isSubmitDisabled} onClick={handleSubmit}>
            Направить
          </Button>
          <Button type="button" theme="secondary" size="lg" disabled={isSubmitting} onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
});

export const ApplicationControlRegionDepartmentsModal = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "assign-region-departments") {
    return null;
  }

  return <RegionDepartmentsModal popupAction={popupAction} />;
});
